import {
  type DefaultError,
  type DefinedInitialDataOptions,
  type DefinedUseQueryResult,
  type QueryClient,
  type QueryKey,
  type UndefinedInitialDataOptions,
  type UseQueryOptions,
  type UseQueryResult,
  // oxlint-disable-next-line no-restricted-imports
  useQuery as useReactQuery,
} from '@tanstack/react-query'

// Based on https://github.com/TanStack/query/blob/edd1bd08e0ef9e55cd2d9339a63843539292fed7/packages/react-query/src/useQuery.ts

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: DefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient
): DefinedUseQueryResult<NoInfer<TData>, TError>

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient
): UseQueryResult<NoInfer<TData>, TError>

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient
): UseQueryResult<NoInfer<TData>, TError>

export function useQuery(
  options: UseQueryOptions,
  queryClient?: QueryClient
): UseQueryResult {
  const queryResult = useReactQuery(options, queryClient)

  // Passing `enabled: false` to a query only prevents the query from running. It may still return cached data (see https://tanstack.com/query/latest/docs/framework/react/guides/disabling-queries). This can be a footgun, where we expect data to be undefined, but a full invalid query is returned (i.e. a full list of Diagnoses is returned if it's cached and it matches the filter where _id is empty). To avoid this, we return undefined for the resulting data if enabled is false since that's probably what most people would expect to happen.
  if (options.enabled === false) {
    // Note we don't use `useMemo` here because queryResult returned from useReactQuery is a new object on every render anyway: https://discord.com/channels/719702312431386674/1403478950965018794/1403650069357396029
    return {
      ...queryResult,
      data: undefined,
    }
  }

  return queryResult
}
